import React, { createContext, useContext, ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'

/**
 * Authentication user info
 */
export interface AuthUser {
  id: string
  email?: string
  displayName?: string
  roles?: string[]
  metadata?: Record<string, any>
}

/**
 * Context value interface
 */
interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  userRoles: string[]
  login: () => void
  logout: () => void
  validateToken: () => Promise<boolean>
  refreshToken: () => Promise<boolean>
  clearError: () => void
}

/**
 * Context creation
 */
const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Provider props
 */
interface AuthProviderProps {
  children: ReactNode
  apiBaseUrl?: string
  /**
   * ros-oauth-enabled master flag. OFF (default) = Cognito Microsoft SSO, today's behavior.
   * ON = the Ros OAuth front door. The flag is a web/PostHog concern, so the value is passed
   * in by the caller rather than read inside shared.
   */
  oauthEnabled?: boolean
}

/**
 * AuthProvider component
 * Provides authentication state for SSO-based access. Cognito (Microsoft Entra ID) by default;
 * the Ros OAuth front door when oauthEnabled is true.
 */
export function AuthProvider({ children, apiBaseUrl, oauthEnabled = false }: AuthProviderProps): JSX.Element {
  const authData = useAuth(apiBaseUrl, oauthEnabled)

  return (
    <AuthContext.Provider value={authData}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to consume auth context.
 * Must be used within an AuthProvider.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return context
}

/**
 * Export context for advanced use cases
 */
export { AuthContext }
